import {Box, Button, Fab, Grid, makeStyles, MenuItem, Snackbar, Typography, useMediaQuery} from '@material-ui/core';
import {Backup} from '@material-ui/icons';
import { Alert } from '@material-ui/lab';
import PolicyImagesCard from 'Agent/Policies/components/PolicyImagesCard';
import {clearPolicy, fetchPolicy, fetchPolicyImages} from 'Agent/Policies/ducks/policy';
import PolicyRouteConstants from 'Agent/Policies/PolicyRouteConstants';
import {getCurrentPage} from 'Core/components/Analytics/Analytics';
import {ANALYTICS_EVENT_ACTION} from 'Core/components/Analytics/AnalyticsEventConstants';
import {changeAnalyticsState, DigitalDataDto, PAGE_ID} from 'Core/ducks/analytics';
import {updatefeatureVariables} from 'Core/ducks/featureToggle';
import {ModalState, showModal} from 'Core/ducks/modal';
import {useBreadcrumbs} from 'Core/hooks/useBreadcrumbs';
import {matchAsync} from 'Core/models/Async';
import {History} from 'history';
import qs from 'query-string';
import React, {useEffect} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {RouteComponentProps, useLocation, withRouter} from 'react-router-dom';
import {Dispatch} from 'redux';
import {ReducerState} from 'rootReducer';
import ErrorPage from 'Shared/components/ErrorPage';
import Loading from 'Shared/components/Loading';
import ActionsMenu from 'Shared/components/PagedView/ActionsMenu';
import RequireScope from 'Shared/components/RequireScope';
import withSelectedAgency, {WithSelectedAgencyProps} from 'Shared/components/withSelectedAgency';
import {Scope} from 'Shared/constants/Scope';
import {selectUserScopes} from 'Users/selectors/userProfileSelectors';
import './PolicyDetails.sass';
import PlanBreakdown, {
  AccountDetails,
  CommissionAgent,
  PolicyAgent,
  PolicyDetail,
  PolicyHolder,
} from './PolicyDetails/PlanBreakdown';
import {PlanInformation} from './PolicyDetails/PlanInformation';
import {PremiumPaymentSummary} from './PolicyDetails/PremiumPaymentSummary';
import { themeColors } from 'Theme';

type PolicyStatusProps = RouteComponentProps<{policyId: string; policyNumber: string}> & WithSelectedAgencyProps;

const usePolicyDetailsStyles = makeStyles(styleTheme => ({
  policyDetails: {
    alignSelf: 'center',
    flexWrap: 'wrap',
    width: '100%',
    [styleTheme.breakpoints.up('lg')]: {
      width: 'calc(80% + 3rem)',
      display: 'flex',
      flexWrap: 'wrap',
    },
  },
  policyDetailTitle: {
    fontWeight: 'bold',
    padding: '1rem 0',
    color: '#333333',
    fontSize: '1.25rem',
  },
  container: {
    width: '100%',
    margin: '1rem 0 0 0',
  },
  actionButton: {
    borderRadius: '0.5rem',
    marginLeft: '0.75rem',
    padding: '0.5rem 1.5rem',
    backgroundColor: themeColors.brightBlue_100,
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    color: themeColors.blue_600,
    fontWeight: 'bold',
    '&:hover': {
      color: themeColors.primaryWhite,
      backgroundColor: themeColors.blue_400,
    },
    [styleTheme.breakpoints.down('sm')]: {
      marginLeft: '0rem',
      marginRight: '0.75rem',
    },
  },
  separator: {
    width: '100%',
    border: '1px solid #ccc',
  },
  header: {
    display: 'flex',
    width: '100%',
    padding: '0 1rem',
    alignItems: 'center',
    justifyContent: 'space-between',
    [styleTheme.breakpoints.down('md')]: {
      marginTop: '1.5rem',
      marginBottom: '0.5rem',
    },
  },
  actionMenuFab: {
    marginLeft: '1rem',
  },
  actionMenu: {
    width: '100%',
    height: '100%',
  },
  policyInformationContainer: {
    margin: '1rem',
    display: 'flex ',
    flexDirection: 'column',
    flexGrow: 1,
  },
  snackBar: {
    borderRadius: 0,
    textAlign: 'justify',
    top: '50%',
    backgroundColor: 'transparent',
    margin: '0 1.5rem 0 .8rem',
    right: 0,
    position: 'relative!important' as 'relative',
    transform: 'translate(-50%, -50%)',
    [styleTheme.breakpoints.down('md')]: {
      width: '25%',
    },

    [styleTheme.breakpoints.down('xs')]: {
      top: '40%',
      transform: 'translate(0%, 0%)',
      width: 'auto',
    },
    [styleTheme.breakpoints.up('md')]: {
      width: '20%',
    },
  },

  alertInfo: {
    display: 'flex',
    color: 'rgba(0, 0, 0, 0.87)',
    alignItems: 'flex-start',
    marginBottom: '0px',
    borderRadius: 0,
    width: '100%',
    backgroundColor: '#D9EEFF',
    '& .MuiAlert-icon': {
      color: '#2196F3',
      width: "20%",
      height: "20%",
      '& .MuiSvgIcon-root': {
        height: '100%',
        width: '100%'
      }
    },
  },
}));

export const enum PolicyDetailActions {
  UPLOAD_DOCUMENT = 'Upload Document',
  VIEW_CLAIMS = 'View Claims',
  VIEW_LETTERS = 'View Letters',
}

export const handleActionButtonClick = (
  action: PolicyDetailActions,
  dispatch: Dispatch,
  analytics: DigitalDataDto,
  policyId: string,
  policyNumber: string,
  eisFlag: boolean,
  history: History
) => {
  switch (action) {
    case PolicyDetailActions.UPLOAD_DOCUMENT: {
      dispatch(
        changeAnalyticsState({
          page: analytics.page,
          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_VIEW_DETAILS_UPLOADDOCUMENT},
        })
      );
      history.push(PolicyRouteConstants.POLICY_UPLOAD_DOCUMENT(policyId, policyNumber));
      break;
    }
    case PolicyDetailActions.VIEW_CLAIMS: {
      dispatch(
        changeAnalyticsState({
          page: analytics.page,
          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_VIEW_DETAILS_VIEWCLAIMS},
        })
      );
      dispatch(showModal(ModalState.ClaimsConfirmation({policyNumber, eisFlag})));
      break;
    }
    case PolicyDetailActions.VIEW_LETTERS: {
      dispatch(
        changeAnalyticsState({
          page: analytics.page,
          ctaResponse: {eventAction: ANALYTICS_EVENT_ACTION.POLICIES_VIEW_DETAILS_VIEWLETTERS},
        })
      );
      history.push(PolicyRouteConstants.CORRESPONDENCE_SUMMARIES(policyNumber));
    }
  }
};

export const PolicyDetails = (props: PolicyStatusProps) => {
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('sm'));
  const styles = usePolicyDetailsStyles();
  const dispatch = useDispatch();
  const scopes = useSelector(selectUserScopes);
  const {policy} = useSelector((state: ReducerState) => state.policy);
  const {match} = props;
  const {policyId, policyNumber} = match.params;
  const {search} = useLocation();
  const parsedQueryParams = qs.parse(search);
  const {selectedAgency} = props;
  const {featureVariables} = useSelector((state: ReducerState) => state.featureToggle);
  const {analytics} = useSelector((state: ReducerState) => state.analytics);
  const location = useLocation();
  const alertStyle1: React.CSSProperties = {position: 'relative', fontSize: '16px', lineHeight: '20px', fontWeight: "700", paddingBottom: "10px"};
  const alertStyle2: React.CSSProperties = {position: 'relative', fontSize: '14px', lineHeight: '18px', textAlign: "left"};

  useBreadcrumbs(policyNumber, props.match.url);

  useEffect(() => {
    dispatch(changeAnalyticsState({page: {pageId: PAGE_ID[getCurrentPage(location.pathname)]}}));
  }, []);

  useEffect(() => {
    dispatch(updatefeatureVariables({...featureVariables, FOOTER_OPEN_DEFAULT: false}));
  }, []);

  useEffect(() => { 
    dispatch(
      fetchPolicy(selectedAgency.encryptedIdentifier, policyId, policyNumber, parsedQueryParams.source as string, selectedAgency.agentNumbers)
    );
    dispatch(fetchPolicyImages(selectedAgency.encryptedIdentifier, policyNumber, policyId, parsedQueryParams.source as string));

    return () => {
      dispatch(clearPolicy());
    };
  }, [policyId, policyNumber]);

  return matchAsync(policy, {
    Conflict: error => <ErrorPage error={error} />,
    Loaded: policy => {
      const [planBenefits] = policy.planBenefits ? Object.values(policy.planBenefits) : [];
      return (
        <div className={styles.policyDetails}>
          <Grid container className={styles.header}>
            <h1 className={styles.policyDetailTitle} data-dd-privacy="mask">
              Policy {policy.policyNumber}
            </h1>
            <Grid item>
              <RequireScope scopes={scopes} scope={Scope.UploadPolicyDocument}>
                {isMobile ? (
                  <Fab
                    aria-label="Upload Document"
                    size="small"
                    color="primary"
                    onClick={() => handleActionButtonClick(
                      PolicyDetailActions.UPLOAD_DOCUMENT,
                      dispatch,
                      analytics,
                      String(policy.id),
                      String(policy.policyNumber),
                      Boolean(policy.eisFlag),
                      props.history
                    )}
                    id="upload-doc-fab"
                  >
                    <Backup />
                  </Fab>
                ) : (
                  <Button
                    className={styles.actionButton}
                    variant="contained"
                    aria-label="Upload Document"
                    startIcon={<Backup />}
                    onClick={() => handleActionButtonClick(
                      PolicyDetailActions.UPLOAD_DOCUMENT,
                      dispatch,
                      analytics,
                      String(policy.id),
                      String(policy.policyNumber),
                      Boolean(policy.eisFlag),
                      props.history
                    )}
                  >
                    Upload Document
                  </Button>
                )}
              </RequireScope>
              {isMobile ? (
                <Fab size="small" className={styles.actionMenuFab} id="action-menu-fab">
                  <ActionsMenu className={styles.actionMenu}>
                    <RequireScope scopes={scopes} scope={Scope.SearchClaims}>
                      <MenuItem
                        aria-label="View Claims"
                        onClick={() => handleActionButtonClick(
                          PolicyDetailActions.VIEW_CLAIMS,
                          dispatch,
                          analytics,
                          String(policy.id),
                          String(policy.policyNumber),
                          Boolean(policy.eisFlag),
                          props.history
                        )}
                        id="view-claims-menu-item"
                      >
                        View Claims
                      </MenuItem>
                    </RequireScope>
                    <RequireScope scopes={scopes} scope={Scope.ViewCorrespondenceDetails}>
                      <MenuItem
                        aria-label="View Correspondences"
                        onClick={() => handleActionButtonClick(
                          PolicyDetailActions.VIEW_LETTERS,
                          dispatch,
                          analytics,
                          String(policy.id),
                          String(policy.policyNumber),
                          Boolean(policy.eisFlag),
                          props.history
                        )}
                        id="view-letters-menu-item"
                      >
                        View Correspondences
                      </MenuItem>
                    </RequireScope>
                  </ActionsMenu>
                </Fab>
              ) : (
                <>
                  <RequireScope scopes={scopes} scope={Scope.SearchClaims}>
                    <Button
                      aria-label="View Claims"
                      className={styles.actionButton}
                      variant="contained"
                      onClick={() => handleActionButtonClick(
                        PolicyDetailActions.VIEW_CLAIMS,
                        dispatch,
                        analytics,
                        String(policy.id),
                        String(policy.policyNumber),
                        Boolean(policy.eisFlag),
                        props.history
                      )}
                    >
                      View Claims
                    </Button>
                  </RequireScope>
                  <RequireScope scopes={scopes} scope={Scope.ViewCorrespondenceDetails}>
                    <Button
                      aria-label="View Correspondences"
                      className={styles.actionButton}
                      variant="contained"
                      onClick={() => handleActionButtonClick(
                        PolicyDetailActions.VIEW_LETTERS,
                        dispatch,
                        analytics,
                        String(policy.id),
                        String(policy.policyNumber),
                        Boolean(policy.eisFlag),
                        props.history
                      )}
                    >
                      View Correspondences
                    </Button>
                  </RequireScope>
                </>
              )}
            </Grid>
            <hr className={styles.separator} />
          </Grid>
          <Grid container spacing={3} className={styles.container}>
            <Grid item xs={12} md={3} role="complementary">
              <PlanBreakdown>
                {policy.insured && <PolicyHolder policyHolder={policy.insured} role="Insured" />}
                {policy.owner && <PolicyHolder policyHolder={policy.owner} role="Owner" />}
                <PolicyDetail
                  status={policy.status}
                  policyNumber={policy.policyNumber}
                  issueDate={policy.issueDate}
                  specialHandlingInstruction={policy.specialHandlingInstruction} />
                <AccountDetails
                  accountName={policy.accountName}
                  accountNumber={policy.accountNumber}
                  previousAccountName={policy.previousAccountName}
                  previousAccountNumber={policy.previousAccountNumber} />
                {policy.agentOfRecord && <PolicyAgent agent={policy.agentOfRecord} agentRole="Agent of Record" />}
                {policy.servicingAgent && <PolicyAgent agent={policy.servicingAgent} agentRole="Servicing Agent" />}
                {Array.isArray(policy.commissionAgents) &&
                  policy.commissionAgents.map((commissionAgent, index) => (
                    <CommissionAgent commissionAgent={commissionAgent} key={`commission-agent_${index}`} />
                  ))}
              </PlanBreakdown>
            </Grid>
            <Grid item xs={12} md={9} role="main">
              <PremiumPaymentSummary
                eisFlag={Boolean(policy.eisFlag)}
                status={policy.status}
                modePremium={policy.premium}
                billPremium={policy.billingPremium}
                lastPayment={policy.lastPayAmount}
                paymentDate={policy.lastPayDate} />
              {policy.lifePlans &&
                policy.lifePlans.map((lifePlan, index) => (
                  <PlanInformation
                    key={`life-policy_${index}`}
                    policyPlan={{ ...lifePlan, planType: 'life' }}
                    eisFlag={Boolean(policy.eisFlag)} />
                ))}
              {policy.healthPlans &&
                policy.healthPlans.map((healthPlan, index) => (
                  <PlanInformation
                    key={`health-policy_${index}`}
                    policyPlan={{ ...healthPlan, planType: 'health' }}
                    eisFlag={Boolean(policy.eisFlag)}
                    sourceName={policy.source?.name}
                    benefits={planBenefits} />
                ))}
              <RequireScope scopes={scopes} scope={Scope.ViewPolicyImges}>
                <PolicyImagesCard selectedAgency={selectedAgency} />
              </RequireScope>
            </Grid>
          </Grid>
          <div className={styles.policyInformationContainer}></div>
        </div>
      );
    },
    Loading: () => <Loading />,
    ServerError: error => <ErrorPage error={error} />,
    Unauthorized: error => <ErrorPage error={error} />,
    NoData: () => <div className="paged-table__content">An error occurred. Please contact Administrator.</div>,
    NoContent: () => <div className="paged-table__content">No Results Available.</div>,
    NotStarted: (error) => (
      <Snackbar
        key={`snackbar_Schoox_loading`}
        anchorOrigin={{vertical: 'top', horizontal: 'center'}}
        open={true}
        className={styles.snackBar}
      >
        <Alert
          severity='info'
          variant="filled"
        className={styles.alertInfo}
        >
          <Box style={{display: 'flex', flexDirection: 'column'}}>
            <Box style={{display: 'flex', flexDirection: 'column'}}>
              <Typography variant="h1" style={alertStyle1}>
                {error.message}
              </Typography>
              <Typography variant="body2" style={alertStyle2}>
                {error.errorDetails}
              </Typography>
            </Box>
          </Box>
        </Alert>
      </Snackbar>
    )
  });
};

export default withSelectedAgency(withRouter(PolicyDetails));
